<script setup lang="ts">
  import { ref, watch, WatchStopHandle } from 'vue'
  import { i18n } from '@/locales/locales'
  import { Employee } from '@/classes/Employee'
  import { useAuthStore } from '@/stores/AuthStore'
  import BaseModal from './BaseModal.vue'
  import presentToast from '@/composables/useToasts'
  import { useConfigStore } from '@/stores/ConfigStore'
  import { storeToRefs } from 'pinia'
  import { useScreenOrientation } from '@/composables/useScreenOrientation'

  const { t } = i18n
  const authStore = useAuthStore()
  const modal = ref<InstanceType<typeof BaseModal>>()
  const enteredPin = ref('')
  const pinConfirmation = ref('')
  const pinChangeRequired = ref(false)
  const name = ref('')

  const { logo_url } = storeToRefs(useConfigStore())

  async function authenticateEmployee(
    employee: Employee,
    noPinChange = false,
  ): Promise<{ success: boolean; pin: string }> {
    resetState()
    modal.value?.open()
    modal.value?.resetTimer()
    return new Promise((resolve) => {
      const watcherList: WatchStopHandle[] = []
      name.value = employee.fullName()
      const resolveAndStop = (result: { success: boolean; pin: string }) => {
        // timeout for 200ms because we use touchstart for performance and dont want to trigger a click on the underlying element
        setTimeout(() => {
          resolve(result)
          watcherList.forEach((stop) => stop())
          resetState()
        }, 200)
      }

      // Use BaseModal's onWillDismiss instead of watching local state
      modal.value?.onWillDismiss().then(() => handleCloseModal(resolveAndStop))

      if (employee.pin_change_required && !noPinChange) {
        pinChangeRequired.value = true
        // alert(t('phrases.pin_change_required'))
        //TODO: notify user that pin change is required
        watcherList.push(
          watch(enteredPin, (newVal) =>
            handlePinChange(newVal, employee, resolveAndStop),
          ),
        )
      } else {
        watcherList.push(
          watch(enteredPin, (newVal) =>
            handlePinEntry(newVal, employee, resolveAndStop),
          ),
        )
      }
    })
  }

  function resetState() {
    console.log('resetState')
    modal.value?.close()
    enteredPin.value = ''
    pinConfirmation.value = ''
    pinChangeRequired.value = false
    name.value = ''
  }

  function handleCloseModal(
    resolve: (value: { success: boolean; pin: string }) => void,
  ) {
    resolve({ success: false, pin: '' })
    resetState()
  }

  function handlePinEntry(
    newPinValue: string,
    employee: Employee,
    resolve: (value: { success: boolean; pin: string }) => void,
  ) {
    console.log('handlePinEntry', newPinValue)
    if (newPinValue.length === 4) {
      if (authStore.checkEmployeePin(employee, newPinValue)) {
        modal.value?.close()
        resolve({ success: true, pin: newPinValue })
      } else {
        void presentToast({
          message: t('phrases.wrong_pin'),
          color: 'danger',
        })
        modal.value?.close()
        resolve({ success: false, pin: '' })
      }
    }
  }

  async function handlePinChange(
    newPinValue: string,
    employee: Employee,
    resolve: (value: { success: boolean; pin: string }) => void,
  ) {
    if (newPinValue.length === 4) {
      if (pinConfirmation.value === '') {
        pinConfirmation.value = newPinValue
        enteredPin.value = ''
      } else if (newPinValue === pinConfirmation.value) {
        const success = await authStore.changeEmployeePin(employee, newPinValue)
        if (success) {
          void presentToast({
            message: t('phrases.new_pin_successfully_changed'),
            color: 'success',
          })
          modal.value?.close()
          resolve({ success, pin: newPinValue })
        } else {
          void presentToast({
            message: t('phrases.something_went_wrong'),
            color: 'danger',
          })
          modal.value?.close()
          resolve({ success: false, pin: '' })
        }
      } else {
        void presentToast({
          message: t('phrases.wrong_pin'),
          color: 'danger',
        })
        modal.value?.close()
        resolve({ success: false, pin: '' })
      }
    }
  }
  let eventAlreadyHandled = false
  function handleTouch(callback: () => void) {
    eventAlreadyHandled = true
    callback()
  }
  function handleClick(callback: () => void) {
    if (eventAlreadyHandled) {
      eventAlreadyHandled = false
      return
    }
    callback()
  }

  function handleNumberEntered(number: number) {
    modal.value?.resetTimer()
    enteredPin.value = enteredPin.value + number
  }

  defineExpose({ authenticateEmployee })

  function handleDelete() {
    if (enteredPin.value.length === 0) return

    enteredPin.value = enteredPin.value.slice(0, -1)
  }

  const { isLandscape } = useScreenOrientation()
</script>

<template>
  <base-modal
    ref="modal"
    :has-timer="true"
    appium="pin-modal"
    :appium-state="modal?.isOpen ? 'open' : 'closed'"
  >
    <div
      class="flex justify-between flex-col gap-y-3 md:gap-y-6 overflow-y-hidden"
    >
      <div v-if="!isLandscape" class="grid grid-cols-3">
        <div
          class="w-32 md:w-44 h-32 md:h-44 p-2 md:p-6 rounded-clockin-big shadow-default justify-self-center col-start-2"
        >
          <img
            v-if="logo_url && logo_url?.length > 0"
            :src="useConfigStore().logo_url"
          />
          <img v-else src="@/assets/Logo.svg" />
        </div>
      </div>
      <span
        v-show="name.length > 0"
        appium="pin-modal-employee-name"
        class="text-center md:text-3xl"
      >
        {{ name }}
      </span>
      <p v-if="!pinChangeRequired" class="self-center md:text-3xl">
        {{ $t('phrases.enter_pin') }}
      </p>
      <p
        v-else-if="pinConfirmation"
        id="pin-confirmation-prompt"
        class="self-center"
      >
        {{ $t('phrases.approve_pin') }}
      </p>
      <p v-else id="enter-new-pin-prompt" class="self-center">
        {{ $t('phrases.enter_new_pin') }}
      </p>
      <div class="flex flex-row gap-2 md:gap-4 items-center justify-center">
        <div
          class="w-3 md:w-8 h-3 md:h-8 border border-gray-800 rounded-full"
          :class="enteredPin.length >= 1 ? 'bg-gray-800' : 'bg-none'"
        ></div>
        <div
          class="w-3 md:w-8 h-3 md:h-8 border border-gray-800 rounded-full"
          :class="enteredPin.length >= 2 ? 'bg-gray-800' : 'bg-none'"
        ></div>
        <div
          class="w-3 md:w-8 h-3 md:h-8 border border-gray-800 rounded-full"
          :class="enteredPin.length >= 3 ? 'bg-gray-800' : 'bg-none'"
        ></div>
        <div
          class="w-3 md:w-8 h-3 md:h-8 border border-gray-800 rounded-full"
          :class="enteredPin.length >= 4 ? 'bg-gray-800' : 'bg-none'"
        ></div>
      </div>

      <div
        class="mt-1 grid grid-cols-3 gap-y-4 gap-x-4 md:gap-x-6 self-center text-2xl md:text-4xl font-bold"
      >
        <button
          v-for="number in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
          :id="`pin-button-${number}`"
          :key="number"
          class="w-14 md:w-24 h-14 md:h-24 border border-solid items-center justify-center flex rounded-full m-auto outline-none active:outline-none active:bg-zinc-100"
          :appium="`pin-button-${number}`"
          @touchstart="handleTouch(() => handleNumberEntered(number))"
          @click="handleClick(() => handleNumberEntered(number))"
        >
          <p class="text-gray-800">{{ number }}</p>
        </button>
        <button
          class="w-14 md:w-24 h-14 md:h-24 border border-solid items-center justify-center flex rounded-full col-start-2 outline-none active:outline-none active:bg-zinc-100"
          :appium="`pin-button-0`"
          @touchstart="handleTouch(() => handleNumberEntered(0))"
          @click="handleClick(() => handleNumberEntered(0))"
        >
          <p class="text-gray-800">
            {{
              // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
              '0'
            }}
          </p>
        </button>
        <button
          class="rounded-full active:bg-zinc-200 flex justify-center items-center"
          @touchstart="handleTouch(handleDelete)"
          @click="handleClick(handleDelete)"
        >
          <font-awesome-icon
            icon="far fa-delete-left"
            class="text-gray-800 opacity-60 mr-1 text-3xl md:text-5xl"
          />
        </button>
      </div>
    </div>
  </base-modal>
</template>
<style scoped>
  ion-modal::part(content) {
    background: transparent;
  }
</style>
